import _, { identity, isError } from 'lodash';
import {
  applySnapshot,
  flow,
  Instance,
  SnapshotOut,
  types,
} from 'mobx-state-tree';
import {
  DongleBindResponse,
  KeyBindResponse,
} from 'services/freak_estimator/Estimator';
import { withEnvironment } from './extensions/with-environment';
import { logger } from 'services/Logger';

const AdressModel = types.model('Address', {
  street: types.string,
  city: types.string,
  zip: types.string,
  country: types.string,
});

const BoardModel = types.model('Board', {
  length: types.optional(types.number, 32),
  width: types.optional(types.number, 8.25),
  nose: types.optional(types.number, 6.875),
  tail: types.optional(types.number, 6.875),
  holes: types.optional(types.number, 2.125),
  wheelbase: types.optional(types.number, 54),
  wheels: types.optional(types.number, 52),
  weight: types.optional(types.number, 2.3),
  manufacturer: types.maybeNull(types.string),
  model: types.maybeNull(types.string),
  deckImg: types.maybeNull(types.string),
});

const BodyModel = types.model('Body', {
  stance: types.maybeNull(types.string),
  height: types.maybeNull(types.number),
  weight: types.maybeNull(types.number),
  nationality: types.maybeNull(types.string), // ✅ NEU: hier auch hinzufügen falls nötig
});

const _AuthUserModel = types // extends '@capacitor-firebase/authentication User'
  .model('AuthUser', {
    uid: types.string,
    displayName: types.maybeNull(types.string),
    email: types.maybeNull(types.string),
    emailVerified: types.boolean,
    isAnonymous: types.boolean,
    phoneNumber: types.maybeNull(types.string),
    photoURL: types.maybeNull(types.string),
    providerId: types.string,
    tenantId: types.maybeNull(types.string),
    refreshToken: types.maybe(types.string),
  });

const _UserModel = _AuthUserModel
  .named('_User')
  .props({
    /** Firestore ID */
    id: types.maybe(types.string),
    realName: types.maybeNull(types.string),
    favoriteRuns: types.array(types.string),
    friendRuns: types.array(types.string),
    // accountType: types.maybeNull(types.string),
    address: types.maybeNull(AdressModel),
    account: types.maybeNull(types.string),
    birthday: types.maybeNull(types.string),
    board: types.optional(BoardModel, {}),
    license: types.maybeNull(types.string),
    bodyInfo: types.maybeNull(BodyModel),
    freak1: types.optional(types.array(types.string), []),
    isAdmin: types.optional(types.boolean, false),
    /** User's total score calculated from their tricks */
    score: types.optional(types.number, 0),
    nationality: types.maybeNull(types.string),
    gender: types.maybeNull(types.string),
  })
  .volatile((_self) => {
    let lastChecked: number | null = null;
    return { lastChecked };
  })
  .actions((self) => ({
    setId(val: string) {
      self.id = val;
    },
  }));

export type AuthUser = Instance<typeof _AuthUserModel>;
export type UserSN = Instance<typeof _UserModel>;

export const UserModel = _UserModel
  .named('User')
  .extend(withEnvironment)
  .actions((self) => {
    const putSelf = () => {
      const cloudUsr = _.pick<UserSN, keyof UserSN>(self, [
        'uid',
        'email',
        'displayName',
        'realName',
        'favoriteRuns',
        'friendRuns',
        'address',
        'birthday',
        'board',
        'bodyInfo',
        'license',
        'isAdmin',
        'score',
        'nationality',
        'gender',
      ]);
      logger.debug('[UserModel.putSelf] Daten werden gespeichert:', cloudUsr);
      return self.environment.cloudStorage
        .putUser(cloudUsr, self.id ?? self.uid)
        .then((result) => {
          logger.debug('[UserModel.putSelf] putUser erfolgreich:', result);
          return result;
        })
        .catch((err) => {
          logger.error('[UserModel.putSelf] putUser FEHLER:', err);
          return identity(err);
        });
    };

    const changeDisplayName = (name: string) => {
      self.displayName = name;
      return putSelf();
    };

    const changeInfo = flow(function* (info: Partial<SnapshotOut<UserSN>>) {
      if (info.license && info.license !== self.license) {
        self.lastChecked = null;
        const response: KeyBindResponse = yield self.environment.api.bindKey(
          info.license
        );
        if (response.text === 'success') self.license = info.license;
      }
      // ✅ FIX: Use _.merge() instead of _.defaultsDeep() for proper null value handling
      const newInfo = _.merge(_.cloneDeep(self), _.omit(info, 'license'));
      applySnapshot(self, newInfo);
    });

    const addFavorite = (id: string, dontPut: boolean = false) => {
      self.favoriteRuns.push(id);
      if (dontPut) return Promise.resolve(null);
      return putSelf();
    };

    const removeFavorite = (id: string) => {
      const idx = self.favoriteRuns.indexOf(id);
      if (idx !== -1) {
        self.favoriteRuns.splice(idx);
        return putSelf();
      }
      return Promise.resolve(false);
    };

    const toggleFavorite = flow(function* (id: string) {
      const remove = self.favoriteRuns.includes(id);
      let ret;
      if (remove) {
        ret = yield removeFavorite(id);
      } else {
        ret = yield addFavorite(id);
      }
      if (isError(ret)) {
        logger.error(ret.message);
        return null;
      }
      return !remove;
    });

    const uploadProfilePhoto = flow(function* (file: File) {
      try {
        logger.debug('[UserModel.uploadProfilePhoto] Starte Upload', {
          userId: self.uid,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          env: self.environment,
        });
        const url = yield self.environment.cloudStorage.uploadProfilePhoto(self.uid, file);
        logger.debug('[UserModel.uploadProfilePhoto] Upload erfolgreich, URL:', url);
        self.photoURL = url;
        // putSelf ist eine lokale Funktion, nicht self.putSelf
        yield Promise.resolve(putSelf());
        logger.debug('[UserModel.uploadProfilePhoto] photoURL gesetzt und User gespeichert:', url);
        return url;
      } catch (err) {
        logger.error('[UserModel.uploadProfilePhoto] Fehler beim Hochladen des Profilbilds:', err);
        throw err;
      }
    });

    return {
      putSelf,
      changeDisplayName,
      changeInfo,
      addFavorite,
      removeFavorite,
      toggleFavorite,
      uploadProfilePhoto,
      addDongle: flow(function* (mac: string) {
        const response: DongleBindResponse =
          yield self.environment.api.bindDongle(mac);
        if (response.text === 'success') {
          return true;
        }
        return false;
      }),
      removeDongle: (mac: string) => {
        self.freak1.remove(mac);
        return putSelf();
      },
      addFriendRun: flow(function* (id: string, dontPut: boolean = false) {
        if (self.friendRuns.includes(id)) return;
        self.friendRuns.push(id);
        if (!dontPut) yield putSelf();
      }),
      updateScore: flow(function* (newScore: number) {
        logger.score('🚨 [UserModel] updateScore() AUFGERUFEN mit:', newScore);
        // Hole den aktuellen Score aus Firestore (getUser)
        let bestScore = newScore;
        try {
          const cloudUser = yield self.environment.cloudStorage.getUser(
            self.uid
          );
          if (cloudUser && typeof cloudUser.score === 'number') {
            if (newScore > cloudUser.score) {
              bestScore = newScore;
              logger.score(
                '[UserModel] Neuer Score ist besser als Firestore-Score:',
                newScore,
                'Firestore:',
                cloudUser.score
              );
            } else {
              bestScore = cloudUser.score;
              logger.score(
                '[UserModel] Firestore-Score bleibt besser oder gleich:',
                cloudUser.score,
                'Neuer Score:',
                newScore
              );
            }
          } else {
            logger.score(
              '[UserModel] Kein Score in Firestore gefunden, benutze neuen Score:',
              newScore
            );
          }
        } catch (err) {
          logger.warn(
            '[UserModel] Fehler beim Abrufen des Firestore-Score:',
            err
          );
        }
        self.score = Math.round(bestScore * 100) / 100; // Round to 2 decimal places
        logger.score(
          '[UserModel] Schreibe Score in Firestore via putSelf()',
          self.score
        );
        try {
          const putResult = yield putSelf();
          logger.success('[UserModel] putSelf() erfolgreich:', {
            score: self.score,
            putResult,
          });
        } catch (err) {
          logger.error('[UserModel] putSelf() FEHLGESCHLAGENE:', {
            score: self.score,
            error: err,
          });
        }
      }),
      addToScore: flow(function* (additionalScore: number) {
        const newScore = self.score + additionalScore;
        self.score = Math.round(newScore * 100) / 100; // Round to 2 decimal places
        yield putSelf();
      }),
      recalculateScore: flow(function* () {
        try {
          // Get all user's tricks from cloud storage
          const userRuns = yield self.environment.cloudStorage.getRunsByUser(
            self.uid
          );

          // Calculate total score from all tricks
          let totalScore = 0;
          userRuns.forEach((trick: any) => {
            if (trick.playData?.ranking?.play_score?.total_score) {
              totalScore += trick.playData.ranking.play_score.total_score;
            }
          });

          // Update the user's score
          self.score = Math.round(totalScore * 100) / 100; // Round to 2 decimal places
          yield putSelf();

          logger.debug('User score recalculated:', {
            uid: self.uid,
            tricksCount: userRuns.length,
            totalScore: self.score,
          });

          return self.score;
        } catch (error) {
          logger.error('Failed to recalculate user score:', error);
          return self.score;
        }
      }),
      setNationality: flow(function* (nationality: string) {
        logger.info(`[User] Setting nationality for user ${self.uid}`, {
          previousNationality: self.nationality,
          newNationality: nationality,
        });

        self.nationality = nationality;

        try {
          yield putSelf();
          logger.success(
            `[User] Nationality successfully set to: ${nationality}`
          );
          return true;
        } catch (error) {
          logger.error(`[User] Failed to save nationality to backend`, {
            error,
            nationality,
            uid: self.uid,
          });
          return false;
        }
      }),
      clearNationality: flow(function* () {
        logger.info(`[User] Clearing nationality for user ${self.uid}`, {
          previousNationality: self.nationality,
        });

        self.nationality = null;

        try {
          yield putSelf();
          logger.success(`[User] Nationality cleared successfully`);
          return true;
        } catch (error) {
          logger.error(`[User] Failed to clear nationality in backend`, {
            error,
            uid: self.uid,
          });
          return false;
        }
      }),
      changeInfoExtended: flow(function* (info: Partial<SnapshotOut<UserSN>>) {
        logger.debug(`[User] Changing user info for ${self.uid}`, {
          fieldsToUpdate: Object.keys(info),
          includesNationality: 'nationality' in info,
          licenseIncoming: info.license,
          licenseCurrent: self.license,
        });

        if (info.license && info.license !== self.license) {
          self.lastChecked = null;
          logger.debug(
            '[UserModel.changeInfoExtended] Rufe api.bindKey auf mit:',
            info.license
          );
          const response: KeyBindResponse = yield self.environment.api.bindKey(
            info.license
          );
          logger.debug(
            '[UserModel.changeInfoExtended] api.bindKey Antwort:',
            response
          );
          if (response.text === 'success') {
            self.license = info.license;
            logger.debug(
              '[UserModel.changeInfoExtended] Lizenz übernommen:',
              self.license
            );
          } else {
            logger.warn(
              '[UserModel.changeInfoExtended] Lizenz NICHT übernommen, API-Response:',
              response
            );
          }
        }

        if (
          info.nationality !== undefined &&
          info.nationality !== self.nationality
        ) {
          logger.info(`[User] Nationality change detected`, {
            from: self.nationality,
            to: info.nationality,
          });
        }

        // ✅ FIX: Use _.merge() instead of _.defaultsDeep() to properly overwrite null values
        // _.defaultsDeep() only sets values that are undefined, not null
        // _.merge() will properly overwrite null values with new data
        const newInfo = _.merge(_.cloneDeep(self), _.omit(info, 'license'));
        logger.debug(
          '[UserModel.changeInfoExtended] applySnapshot mit (nach merge):',
          {
            originalInfo: _.omit(info, 'license'),
            originalSelf: { gender: self.gender, nationality: self.nationality },
            mergedInfo: { gender: newInfo.gender, nationality: newInfo.nationality },
            fullMergedInfo: newInfo
          }
        );
        applySnapshot(self, newInfo);

        try {
          logger.debug(
            '[UserModel.changeInfoExtended] Vor putSelf() mit Lizenz:',
            self.license
          );
          yield putSelf();
          logger.success(`[User] User info updated successfully`);
          return true;
        } catch (error) {
          logger.error(`[User] Failed to save updated user info`, {
            error,
            uid: self.uid,
            updateFields: Object.keys(info),
          });
          return false;
        }
      }),
      query: flow(function* () {
        const cloudUser: UserSN | null = yield self.environment.cloudStorage
          .getUser(self.uid)
          .catch(identity);
        if (!cloudUser || isError(cloudUser)) {
          logger.error(`No User with Uid ${self.uid} in database!`);
          return;
        }

        // At this point `self` is the `Auth User` object after a login
        // combined with some defaults from `models/UserModel`
        //
        // apple sign in has no displayName for the first login
        //   => usr.displayName will be null
        //
        // deep merge `cloudUser` and `self` so that
        // `cloudUser` overwrites values in `self` BUT only if `cloudUser.value != nil`
        // and the merged object doesn't have keys that weren't present in `self`
        //   (values that we didn't expect the `userModel` to have!)
        // ✅ FIX: Use _.merge() to properly overwrite null values from backend
        // ✅ DEBUG: Log gender/nationality before and after merge
        logger.debug('[UserModel.query] Before merge:', {
          selfGender: self.gender,
          selfNationality: self.nationality,
          cloudUserGender: cloudUser.gender,
          cloudUserNationality: cloudUser.nationality,
        });
        
        const filteredCloudUser = _.pick(cloudUser, _.keys(self));
        const mergedUser = _.merge(
          _.cloneDeep(self),
          _.omitBy(filteredCloudUser, _.isNil)
        );
        _.assign(self, mergedUser);
        
        // ✅ DEBUG: Log gender/nationality after merge
        logger.debug('[UserModel.query] After merge:', {
          selfGender: self.gender,
          selfNationality: self.nationality,
          mergedUserGender: mergedUser.gender,
          mergedUserNationality: mergedUser.nationality,
        });
        // --- PATCH: Score-Merge-Logik ---
        if (typeof cloudUser.score === 'number') {
          const oldScore = self.score;
          self.score = Math.max(self.score ?? 0, cloudUser.score ?? 0);
          logger.debug('[UserModel.query] Score-Merge:', {
            oldScore,
            cloudScore: cloudUser.score,
            result: self.score,
          });
        }

        logger.debug(`[User] User data merged from cloud`, {
          uid: self.uid,
          nationality: self.nationality,
          hasNationality: !!self.nationality,
        });
      }),
    };
  })
  .views((self) => ({
    get hasNationality() {
      return !!self.nationality;
    },

    get nationalityDisplay() {
      return self.nationality || 'Not set';
    },

    get userProfile() {
      return {
        uid: self.uid,
        displayName: self.displayName,
        email: self.email,
        nationality: self.nationality,
        score: self.score,
        realName: self.realName,
      };
    },
  }));

export type User = Instance<typeof UserModel>;
export type UserSnapshot = SnapshotOut<typeof UserModel>;
